import { useCallback, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./css/custom.css";
import { useRoutes } from "react-router-dom";
import { useSelector } from "react-redux";
import getRoutes from "./routes";
import {
  usePreventZoom,
  useOnlineStatus,
  useNetworkQuality,
  useFetchAndSyncData,
} from "./hooks";
import { manageErrorLogs } from "./helpers/commonFunction";
import { UpdateAppModal } from "./components/Modals";

const App = () => {
  usePreventZoom();
  const { isOnline } = useOnlineStatus();
  const { checkNetwork } = useNetworkQuality({});
  const { uploadAndFetchData } = useFetchAndSyncData(false);

  const localSUser = JSON.parse(localStorage.getItem("user"));
  const { user } = useSelector((state) => state.userReducer);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  // 1 Min script for upload IDB data to Server
  useEffect(() => {
    if (isOnline && user?.user_id) {
      const intervalId = setInterval(
        handleUploadDBData,
        process.env.REACT_APP_API_CALL_TIME,
      );
      return () => clearInterval(intervalId);
    }
  }, [isOnline, user]);
  const handleUploadDBData = useCallback(async () => {
    try {
      const network = await checkNetwork();
      if (network.status === "poor") return;
      await uploadAndFetchData("");
    } catch (error) {
      manageErrorLogs(error);
    }
  }, []);

  // Fallback version check in case service worker isn't controlling the page
  useEffect(() => {
    if (!isOnline) return;
    const checkVersionFallback = async () => {
      try {
        const response = await fetch(`/version.json`, { cache: "no-store" });
        if (!response.ok) return;

        const data = await response.json();
        const newVersion = data?.version;
        if (!newVersion) return;

        const prevVersion = localStorage.getItem("app_version");
        const swHasController = !!(
          navigator?.serviceWorker && navigator.serviceWorker.controller
        );
        if (prevVersion && newVersion !== prevVersion && !swHasController) {
          localStorage.setItem("app_version", newVersion);
          setShowUpdatePopup(true);
        }
      } catch (error) {
        manageErrorLogs(error);
      }
    };
    checkVersionFallback();
    const intervalId = setInterval(
      checkVersionFallback,
      process.env.REACT_APP_API_CALL_TIME,
    );
    return () => clearInterval(intervalId);
  }, [isOnline]);
  const handleRefreshUpdate = useCallback(async () => {
    setIsUpdateLoading(true);
    try {
      localStorage.setItem("app_update_pending", "true");
      const registration = await navigator.serviceWorker?.getRegistration?.();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        await new Promise((resolve) => {
          const checkActivation = () => {
            if (
              registration.active &&
              registration.active !== registration.waiting
            ) {
              resolve();
            } else {
              setTimeout(checkActivation, 100);
            }
          };
          checkActivation();
        });
      }
      localStorage.removeItem("offline_mode_notification_shown");
      window.location.reload(true);
    } catch (error) {
      setIsUpdateLoading(false);
      setShowUpdatePopup(false);
      manageErrorLogs(error);
    }
  }, []);

  const routing = useRoutes(getRoutes(localSUser ? user : null));
  return (
    <>
      {routing}
      {showUpdatePopup && (
        <UpdateAppModal
          onRefresh={handleRefreshUpdate}
          isLoading={isUpdateLoading}
        />
      )}
    </>
  );
};

export default App;
